import React from 'react'
import { connect } from 'react-redux'
import { Grid, Typography, withStyles } from '@material-ui/core'
import DateTimePicker from './DateTimePicker'
import CampaignSelect from './CampaignSelect'
import StageSelect from './StageSelect'
import Delay from './Delay'
import TimePicker from './TimePicker'
import language from 'utils/vast-languagepack'
import { taskTypeNameToColloquialMap } from 'utils/taskTypeIDToNameMap'
import Select from './Select'
import FromEmailSelect from './FromEmailSelect'
import FromName from './FromName'

const ConnectedSelect = connect(
	(state) => ({
		recipientSelectValueLength: state.tasks.addOrEdit.select?.value?.length,
	}),
	null,
)(({ recipientSelectValueLength }) => (
	<Select warning={!recipientSelectValueLength} />
))

const TaskSend = connect(
	(state) => ({
		selectedActionPrediction:
			state.tasks.addOrEdit.fields.selectedActionPrediction.value,
		recipientSelectValueLength: state.tasks.addOrEdit.select?.value?.length,
	}),
	null,
)(({ selectedActionPrediction, recipientSelectValueLength }) => {
	const obj = {
    email: 'When should we send this email?',
    sms: 'When should we send this text message?',
    groupAdd: `When should we add ${
      recipientSelectValueLength === 1 ? 'this contact' : 'these contacts'
    } to a group?`,
    groupRemove: `When should we remove ${
      recipientSelectValueLength === 1 ? 'this contact' : 'these contacts'
    } from a group?`,
    groupMove: `When should we move ${
      recipientSelectValueLength === 1 ? 'this contact' : 'these contacts'
    } between groups`,
    reassignContact: 'When should we reassign these contacts?',
  }

	return (
		<Typography variant="h5" gutterBottom>
			{obj[selectedActionPrediction] || 'When should we execute this task?'}
		</Typography>
	)
})

export default connect((state) => {
	const task = state.tasks.tasksByID[state.tasks.addOrEdit.taskID]
	const stats = task?.sms_stats || task?.email_stats
	const campaignStatus =
		state.tasks.addOrEdit.originalCampaignSelected?.campaign?.status
	const automationTypeSelected = state.tasks.addOrEdit.automationTypeSelected
	const disabled = Boolean(
		(stats && automationTypeSelected !== 'stage') ||
			campaignStatus === 'L' ||
			campaignStatus === 'C',
	)

	return {
		automationTypeSelected: state.tasks.addOrEdit.automationTypeSelected,
		splitFormDisplayed: state.tasks.addOrEdit.splitFormDisplayed,
		disabled,
		selectedActionPrediction:
			state.tasks.addOrEdit?.fields?.selectedActionPrediction?.value,
		dateValue: state.tasks.addOrEdit.fields.date.value,
		delay: state.tasks.addOrEdit.delay,
		delayUnit: state.tasks.addOrEdit.delayUnit,
		step: state.view.stepper.task,
	}
}, null)(
	withStyles((theme) => ({
		delayTypography: {
			display: 'inline-block',
			top: 19,
			position: 'absolute',
			[theme.breakpoints.down('lg')]: {
				display: 'block',
				position: 'static',
				top: 8,
			},
		},
		delay: {
			display: 'inline-block',
			width: 'calc(100% - 170.45px)',
			marginLeft: '170.45px',
			[theme.breakpoints.down('lg')]: {
				display: 'block',
				width: '100%',
				marginLeft: 0,
			},
		},
		timeOfDay: {
			display: 'inline-block',
			width: 'calc(100% - 202.45px)',
			marginLeft: '202.45px',
			[theme.breakpoints.down('lg')]: {
				display: 'block',
				width: '100%',
				marginLeft: 0,
			},
		},
		delayWithoutTypography: {
			display: 'block',
			width: '100%',
		},
	}))(
		({
			typography,
			automationTypeSelected,
			setDelay,
			classes,
			splitFormDisplayed,
			disabled,
			selectedActionPrediction,
			dateValue,
			delay,
			delayUnit,
			delayCallToAction,
			scheduleCallToAction,
			step,
		}) => {
			return (
        <Grid container spacing={16}>
          {disabled ? (
            <Grid item>
              <div>
                {automationTypeSelected === 'scheduled' ||
                automationTypeSelected === 'calendar' ? (
                  <div>
                    This{' '}
                    <strong>
                      {taskTypeNameToColloquialMap?.[
                        selectedActionPrediction
                      ]?.colloquial?.toLowerCase?.()}
                    </strong>{' '}
                    task {dateValue > new Date() ? 'will fire' : 'fired'} off on{' '}
                    {dateValue?.toLocaleString?.() || 'a specific date'}.
                  </div>
                ) : (
                  <div>
                    This{' '}
                    <strong>
                      {taskTypeNameToColloquialMap?.[
                        selectedActionPrediction
                      ]?.colloquial?.toLowerCase?.()}
                    </strong>{' '}
                    task is delayed for {`${delay} ${delayUnit}`}.
                  </div>
                )}
              </div>
            </Grid>
          ) : null}
          {delayCallToAction && selectedActionPrediction !== 'email' && (
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                Set how long to delay your task.
              </Typography>
            </Grid>
          )}
          {scheduleCallToAction && (
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                Schedule your task here.
              </Typography>
            </Grid>
          )}
          {automationTypeSelected !== 'scheduled' ? (
            <Grid item xs={12}>
              {typography ? (
                <Typography variant="h5" gutterBottom>
                  {language.task.add.campaignSelect}
                </Typography>
              ) : null}
              <CampaignSelect />
            </Grid>
          ) : null}
          {automationTypeSelected === 'stage' ? (
            <Grid item xs={12}>
              {typography ? (
                <Typography variant="h5" gutterBottom>
                  {language.task.add.funnelSelect}
                </Typography>
              ) : null}
              <StageSelect />
            </Grid>
          ) : (
            <Grid item xs={12}>
              {typography ? <TaskSend /> : null}
              <DateTimePicker />
            </Grid>
          )}
          {automationTypeSelected !== 'stage' &&
            selectedActionPrediction === 'sms' && (
              <Grid item xs={12}>
                {typography ? (
                  <Typography variant="h5" gutterBottom>
                    Who do you want to send this text message to?
                  </Typography>
                ) : null}
                <Select />
              </Grid>
            )}
          {automationTypeSelected !== 'stage' &&
            selectedActionPrediction === 'email' &&
            step !== 3 && (
              <Grid item xs={12}>
                {typography ? (
                  <Typography variant="h5" gutterBottom>
                    Who do you want to send this email to?
                  </Typography>
                ) : null}
                <ConnectedSelect />
              </Grid>
            )}
          {selectedActionPrediction === 'email' && step !== 3 && (
            <>
              <Grid item xs={12}>
                {typography ? (
                  <Typography variant="h5" gutterBottom>
                    Which email address should we send this email from?
                  </Typography>
                ) : null}
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      color: 'rgba(0, 0, 0, 0.54)',
                      fontSize: ' 0.75rem',
                      backgroundColor: '#fff',
                      zIndex: 1,
                      position: 'absolute',
                      left: 40,
                      top: -5,
                      padding: '0 5px',
                    }}
                  >
                    From:
                  </div>
                  <FromEmailSelect />
                </div>
              </Grid>
              <Grid item xs={12}>
                {typography ? (
                  <Typography variant="h5" gutterBottom>
                    How would you like your name to appear in the email?
                  </Typography>
                ) : null}
                <FromName />
              </Grid>
            </>
          )}
          {automationTypeSelected === 'stage' ? (
            <>
              <Grid style={{ position: 'relative' }} item xs={12}>
                {typography ? (
                  <Typography
                    className={classes.delayTypography}
                    variant="h5"
                    gutterBottom
                  >
                    {language.task.add.delayTask}
                  </Typography>
                ) : null}
                <div
                  className={
                    typography ? classes.delay : classes.delayWithoutTypography
                  }
                >
                  <Delay />
                </div>
              </Grid>
              <Grid
                style={{ position: 'relative', marginTop: 19 }}
                item
                xs={12}
              >
                {typography ? (
                  <Typography
                    className={classes.delayTypography}
                    variant="h5"
                    gutterBottom
                  >
                    Add a time of day
                  </Typography>
                ) : null}
                <div
                  className={
                    typography
                      ? classes.timeOfDay
                      : classes.delayWithoutTypography
                  }
                >
                  <TimePicker />
                </div>
              </Grid>
            </>
          ) : null}
        </Grid>
      )
		},
	),
)
